import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import debounce from 'lodash.debounce';
import { CellContent, CellWithoutDataContent, EllipsisText, StyledInput } from '../styles';

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-height: 200px;
  overflow-y: auto;
  display: ${props => props.show ? 'block' : 'none'};
`;

const OptionItem = styled.div`
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 1rem;
  font-family: inherit;
  font-weight: 400;
  color: var(--color-secondary-text);
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }

  ${props => props.highlighted && `
    background-color: #e2e8f0;
  `}
`;

const LoadingItem = styled.div`
  padding: 0.5rem 0.75rem;
  font-size: 1rem;
  font-family: inherit;
  color: #888;
  text-align: center;
`;

function AddressInput({
  value: propValue = '',
  editable: propEditable = false,
  onChange,
  left,
  isMissing,
  isSelected = false,
}) {
  const [value, setValue] = useState(propValue);
  const [initialValue, setInitialValue] = useState(propValue);
  const [editable, setEditable] = useState(propEditable);
  const [options, setOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const geocodeAddress = useCallback((query) => {
    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode(
        {
          address: query,
          componentRestrictions: {
            country: 'US',
          },
        },
        (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const options = results.map((result) => ({
              label: result.formatted_address,
              value: result.formatted_address,
              result
            }));
            setOptions(options);
          } else {
            console.log('Geocoder failed:', status);
            setOptions([]);
          }
          setIsLoading(false);
        }
      );
    } else {
      console.log('Google Maps API not available');
      setOptions([]);
      setIsLoading(false);
    }
  }, []);

  const debouncedGeocode = useCallback(
    debounce((query) => {
      geocodeAddress(query);
    }, 400, { leading: false, trailing: true }),
    [geocodeAddress]
  );

  useEffect(() => {
    if (propValue !== initialValue && !propEditable) {
      setValue(propValue);
      setInitialValue(propValue);
      setEditable(false);
    }
  }, [propValue, propEditable, initialValue]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);

    if (newValue.length >= 3) {
      setIsLoading(true);
      setShowDropdown(true);
      setOptions([]);
      setHighlightedIndex(-1);

      debouncedGeocode(newValue);
    } else {
      setShowDropdown(false);
      setOptions([]);
      setIsLoading(false);
    }
  };

  const elementContains = (container, element) => {
    if (!container || !element) return false;
    if (typeof container.contains === 'function') {
      return container.contains(element);
    }
    let node = element.parentNode;
    while (node != null) {
      if (node === container) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  };

  const handleBlur = (e) => {
    setTimeout(() => {
      const currentContainer = containerRef.current;
      const activeElement = document.activeElement;

      if (!currentContainer || !activeElement || !elementContains(currentContainer, activeElement)) {
        const newValue = e.target ? e.target.value : value;
        setEditable(false);
        setShowDropdown(false);
        setHighlightedIndex(-1);
        setIsLoading(false);

        if (onChange && newValue !== initialValue) {
          onChange(newValue);
        }
      }
    }, 150);
  };

  const handleKeyDown = (e) => {
    if (showDropdown && options.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev =>
            prev < options.length - 1 ? prev + 1 : 0
          );
          return;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : options.length - 1
          );
          return;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0) {
            selectOption(options[highlightedIndex]);
          } else {
            check();
          }
          return;
        case 'Escape':
          setShowDropdown(false);
          setHighlightedIndex(-1);
          setIsLoading(false);
          if (inputRef.current) {
            inputRef.current.blur();
          }
          return;
        default:
          break;
      }
    }

    if (e.key === 'Enter') {
      check();
    }
  };

  const check = () => {
    setEditable(false);
    setShowDropdown(false);
    setIsLoading(false);
    if (onChange) {
      onChange(value);
    }
  };

  const edit = () => {
    setEditable(true);
  };

  const selectOption = (option) => {
    setValue(option.value);
    setShowDropdown(false);
    setHighlightedIndex(-1);
    setEditable(false);
    setIsLoading(false);

    if (onChange) {
      onChange(option.value);
    }
  };

  return (
    <InputContainer ref={containerRef}>
      {editable ? (
        <CellContent>
          <StyledInput
            ref={inputRef}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoFocus
            autoComplete="off"
            placeholder="Enter address..."
          />

          <DropdownContainer show={showDropdown}>
            {isLoading ? (
              <LoadingItem>Searching addresses...</LoadingItem>
            ) : options.length > 0 ? (
              options.map((option, index) => (
                <OptionItem
                  key={index}
                  highlighted={index === highlightedIndex}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {option.label}
                </OptionItem>
              ))
            ) : (
              value.length >= 3 && !isLoading && (
                <LoadingItem>No addresses found</LoadingItem>
              )
            )}
          </DropdownContainer>
        </CellContent>
      ) : (
        <>
          {value ? (
            <EllipsisText left={left} onClick={edit} isSelected={isSelected}>
              {value}
            </EllipsisText>
          ) : (
            <CellWithoutDataContent left={left} onClick={edit} isMissing={isMissing} />
          )}
        </>
      )}
    </InputContainer>
  );
}

AddressInput.propTypes = {
  value: PropTypes.string,
  editable: PropTypes.bool,
  onChange: PropTypes.func,
  left: PropTypes.bool,
};

AddressInput.defaultProps = {
  value: '',
  editable: false,
  onChange: null,
  left: false,
};

export default AddressInput;
